'use client'

import { Button } from '@/components/ui/button'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import { Link } from '@/i18n/navigation'
import Autoplay from 'embla-carousel-autoplay'
import { ArrowUpRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useRef } from 'react'
import type { ProductItemConfig } from './an-truong.constants'

interface ProductFeatureSectionProps {
  product: ProductItemConfig
}

const SPEC_KEYS = ['material', 'form', 'feature', 'suitable'] as const

const AUTOPLAY_CONFIG = {
  delay: 3000,
  stopOnInteraction: false,
}

export const ProductFeatureSection = ({
  product,
}: ProductFeatureSectionProps) => {
  const t = useTranslations('landing_an_truong')
  const autoplay = useRef(Autoplay(AUTOPLAY_CONFIG))

  const galleryImages = [
    product.images.main,
    product.images.detail,
    product.images.wear,
  ]

  return (
    <section
      id={product.id}
      className='w-full scroll-mt-20 bg-white px-4 py-12 lg:p-[60px]'
    >
      <div className='flex flex-col gap-6 lg:gap-14'>
        {/* Tagline */}
        <h2 className='max-w-[1400px] font-criteria text-2xl font-semibold leading-[35px] tracking-[-0.48px] text-neutral-900 lg:text-[65px] lg:font-medium lg:leading-[90px] lg:tracking-[-1.3px]'>
          {t.rich(`products.${product.key}.tagline`, {
            hl: (chunks) => <span className='text-[#273bcd]'>{chunks}</span>,
          })}
        </h2>

        {/* Content - 6/6 Grid on desktop */}
        <div className='grid grid-cols-1 items-start gap-6 lg:grid-cols-12 lg:gap-24'>
          {/* Gallery Column (6 cols on desktop) */}
          <div
            className={`col-span-12 flex flex-col lg:col-span-6 ${
              product.reverse ? 'lg:order-2' : 'lg:order-1'
            }`}
          >
            {/* Gallery - Mobile (Carousel slide with autoplay) */}
            <div className='w-full lg:hidden'>
              <Carousel
                opts={{
                  align: 'start',
                  loop: true,
                }}
                plugins={[autoplay.current]}
                className='w-full'
              >
                <CarouselContent className='-ml-2'>
                  {galleryImages.map((src, idx) => (
                    <CarouselItem key={idx} className='basis-[288px] pl-2'>
                      <div className='relative h-[350px] w-full overflow-hidden rounded-xl bg-neutral-100'>
                        <Image
                          src={src}
                          alt={`${t(`products.${product.key}.name`)} ${idx + 1}`}
                          fill
                          sizes='280px'
                          className='pointer-events-none object-cover'
                        />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>

            {/* Gallery - Desktop (1 large + 2 thumbs) */}
            <div className='hidden w-full flex-col gap-4 lg:flex'>
              <div className='relative aspect-[840/1050] w-full overflow-hidden rounded-xl bg-neutral-100'>
                <Image
                  src={product.images.main}
                  alt={`${t(`products.${product.key}.name`)} main`}
                  fill
                  sizes='(max-width: 1024px) 100vw, 50vw'
                  className='pointer-events-none object-cover'
                />
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div className='relative aspect-square w-full overflow-hidden rounded-xl bg-neutral-100'>
                  <Image
                    src={product.images.detail}
                    alt={`${t(`products.${product.key}.name`)} detail`}
                    fill
                    className='pointer-events-none object-cover'
                  />
                </div>
                <div className='relative aspect-square w-full overflow-hidden rounded-xl bg-neutral-100'>
                  <Image
                    src={product.images.wear}
                    alt={`${t(`products.${product.key}.name`)} wear`}
                    fill
                    className='pointer-events-none object-cover'
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Info Column (6 cols on desktop, sticky) */}
          <div
            className={`col-span-12 flex flex-col items-start lg:sticky lg:top-24 lg:col-span-6 lg:self-start ${
              product.reverse ? 'lg:order-1' : 'lg:order-2'
            }`}
          >
            {/* Name & Kind */}
            <h3 className='font-criteria text-2xl font-semibold leading-[35px] tracking-[-0.48px] text-neutral-900 lg:text-4xl lg:leading-[47px] lg:tracking-[-0.72px]'>
              {t(`products.${product.key}.name`)}
            </h3>
            <p className='mt-1 font-sans text-sm leading-6 text-neutral-500 lg:text-base'>
              {t(`products.${product.key}.kind`)}
            </p>

            {/* Price */}
            <div className='mt-4 flex flex-col gap-1 lg:mt-7'>
              {product.hasDiscount && (
                <span className='font-sans text-sm text-neutral-400 line-through lg:text-base'>
                  {t(`products.${product.key}.price_original`)}
                </span>
              )}
              <div className='flex items-center gap-3 lg:gap-4'>
                <span className='font-sans text-2xl font-bold text-neutral-900 lg:text-[28px]'>
                  {t(`products.${product.key}.price_current`)}
                </span>
                {product.hasDiscount && (
                  <span className='rounded-full bg-[#273bcd] px-2 py-0.5 font-sans text-xs font-bold text-white lg:px-2 lg:py-1 lg:text-base'>
                    {t(`products.${product.key}.discount`)}
                  </span>
                )}
              </div>
            </div>

            {/* Scene */}
            <p className='mt-3 font-sans text-sm leading-6 text-neutral-700 lg:mt-5 lg:text-lg'>
              {t(`products.${product.key}.scene`)}
            </p>

            {/* Specs */}
            <div className='mt-5 flex w-full flex-col gap-3.5 border-y border-neutral-200 py-5 lg:mt-7'>
              {SPEC_KEYS.map((spec) => (
                <div key={spec} className='flex items-start gap-4'>
                  <span className='w-24 shrink-0 font-sans text-sm text-neutral-500 lg:w-[110px] lg:text-base'>
                    {t(`products.${product.key}.specs.${spec}_label`)}
                  </span>
                  <span className='flex-1 font-sans text-sm font-medium text-[#273bcd] lg:text-base'>
                    {t(`products.${product.key}.specs.${spec}_val`)}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className='mt-6 w-full lg:mt-8 lg:w-auto'>
              <Button
                variant='black'
                className='h-auto w-full rounded-full px-10 py-3.5 font-criteria text-base uppercase tracking-wider lg:w-auto'
                asChild
              >
                <Link href={product.href}>
                  <span>{t(`products.${product.key}.cta`)}</span>
                  <ArrowUpRight className='size-5' />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
